import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Video, Users, Mail, Plus, Edit2, Trash2, Send, X, Check, Upload, Play, FileText, Image, ChevronDown, ChevronRight, DollarSign, Sparkles, Shield, ListChecks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AdminWelcomeVideo from './AdminWelcomeVideo';
import TourSystem, { EnhancedTourButton } from './TourSystem';
import ExamBuilder from './ExamBuilder';

const AdminContentManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('welcome-video');
  const [showAdminTour, setShowAdminTour] = useState(false);
  const [courses, setCourses] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [lessons, setLessons] = useState({});
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // File input refs
  const videoInputRef = useRef(null);
  const ebookFileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // Modal state
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showEbookModal, setShowEbookModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentCourseId, setCurrentCourseId] = useState(null);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [lessonResources, setLessonResources] = useState([]);
  const [resourceUploading, setResourceUploading] = useState(false);
  const resourceFileInputRef = useRef(null);
  
  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    track: 'apostolic-prophetic',
    duration: '',
    level: 'beginner',
    thumbnail_url: '',
    is_published: false
  });
  
  // Lesson form state
  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    order: 1,
    video_url: '',
    drive_file_id: '',
    duration_minutes: 30,
    is_published: false
  });
  
  // Ebook form state
  const [ebookForm, setEbookForm] = useState({
    title: '',
    author: '',
    description: '',
    category: '',
    page_count: '',
    file_url: '',
    thumbnail_url: '',
    is_premium: false,
    price: ''
  });

  // Exam builder modal state
  const [examBuilderOpen, setExamBuilderOpen] = useState(false);
  const [examBuilderLesson, setExamBuilderLesson] = useState(null);
  const [examBuilderCourseId, setExamBuilderCourseId] = useState(null);
  const [examBuilderExisting, setExamBuilderExisting] = useState(null);
  const [examLoading, setExamLoading] = useState(null); // lesson.id while loading

  const openLessonExam = async (lesson, courseId) => {
    setExamLoading(lesson.id);
    // Reset stale state BEFORE the fetch
    setExamBuilderOpen(false);
    setExamBuilderLesson(null);
    setExamBuilderCourseId(null);
    setExamBuilderExisting(null);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${backendUrl}/api/exams/lesson/${lesson.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Failed to load exam (${res.status})`);
        return;
      }
      setExamBuilderLesson({
        lesson_id: lesson.id,
        lesson_title: lesson.title,
      });
      setExamBuilderCourseId(courseId);
      setExamBuilderExisting(data.exam || null);
      setExamBuilderOpen(true);
    } catch (e) {
      console.error('Failed to open exam:', e);
      toast.error('Failed to open exam — check connection');
    } finally {
      setExamLoading(null);
    }
  };

  const tabs = [
    { id: 'welcome-video', label: 'Welcome Video', icon: Video },
    { id: 'courses', label: 'Courses & Lessons', icon: Play },
    { id: 'ebooks', label: 'Ebooks', icon: BookOpen },
    { id: 'uploads', label: 'File Uploads', icon: Upload },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'user-access', label: 'User Access', icon: Shield },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'emails', label: 'Email Campaigns', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Sparkles }
  ];

  // Handle tab click - navigate to dedicated page for payments and user-access
  const handleTabClick = (tabId) => {
    if (tabId === 'payments') {
      navigate('/admin/payments');
    } else if (tabId === 'user-access') {
      navigate('/admin/user-access');
    } else {
      setActiveTab(tabId);
    }
  };

  // Enable mentor mode for current user
  const enableMentorMode = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // First, get current user info from API
      const meResponse = await fetch(`${backendUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!meResponse.ok) {
        alert('Could not get your user info. Please log out and log back in.');
        return;
      }
      
      const currentUser = await meResponse.json();
      
      if (!currentUser?.email) {
        alert('Could not determine your email. Please log out and log back in.');
        return;
      }
      
      // Now update the role
      const response = await fetch(`${backendUrl}/api/admin/override/update-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_email: currentUser.email,
          role: 'mentor',
          reason: 'Self-enabled mentor mode'
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        alert('✅ Mentor mode enabled! You will now receive appointment notifications. Please refresh the page.');
        window.location.reload();
      } else {
        alert(`Failed: ${result.detail || JSON.stringify(result)}`);
      }
    } catch (error) {
      console.error('Error enabling mentor mode:', error);
      alert('Failed to enable mentor mode: ' + error.message);
    }
  };

  // Seed community channels
  const seedCommunityChannels = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        alert('You are not logged in. Please log in first.');
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/seed-community-channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        alert(`✅ ${result.message || 'Channels seeded successfully!'}\n\nChannels created: ${result.channels_count || 'Unknown'}`);
      } else {
        alert(`❌ Failed: ${result.detail || JSON.stringify(result)}`);
      }
    } catch (error) {
      console.error('Error seeding channels:', error);
      alert('Failed to seed community channels: ' + error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      if (activeTab === 'courses') {
        const response = await fetch(`${backendUrl}/api/admin/content/courses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCourses(data);
        }
      } else if (activeTab === 'ebooks') {
        const response = await fetch(`${backendUrl}/api/admin/content/ebooks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setEbooks(data);
        }
      } else if (activeTab === 'users') {
        const response = await fetch(`${backendUrl}/api/admin/content/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.users || []);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lessons for a course
  const fetchLessons = async (courseId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/courses/${courseId}/lessons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLessons(prev => ({ ...prev, [courseId]: data.lessons || [] }));
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    }
  };

  // Toggle course expansion
  const toggleCourseExpand = (courseId) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
    } else {
      setExpandedCourse(courseId);
      if (!lessons[courseId]) {
        fetchLessons(courseId);
      }
    }
  };

  // Lesson handlers
  const resetLessonForm = () => {
    setLessonForm({
      title: '',
      description: '',
      order: 1,
      video_url: '',
      drive_file_id: '',
      duration_minutes: 30,
      is_published: false
    });
    setEditingItem(null);
  };

  const openAddLesson = (courseId) => {
    resetLessonForm();
    setCurrentCourseId(courseId);
    // Set order to next available
    const courseLessons = lessons[courseId] || [];
    setLessonForm(prev => ({ ...prev, order: courseLessons.length + 1 }));
    setShowLessonModal(true);
  };

  const openEditLesson = (lesson) => {
    setLessonForm({
      title: lesson.title || '',
      description: lesson.description || '',
      order: lesson.order || 1,
      video_url: lesson.video_url || '',
      drive_file_id: lesson.drive_file_id || '',
      duration_minutes: lesson.duration_minutes || 30,
      is_published: lesson.is_published || false
    });
    setEditingItem(lesson);
    setCurrentCourseId(lesson.course_id);
    setShowLessonModal(true);
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/courses/${currentCourseId}/lessons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...lessonForm, course_id: currentCourseId })
      });

      if (response.ok) {
        fetchLessons(currentCourseId);
        setShowLessonModal(false);
        resetLessonForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create lesson');
      }
    } catch (error) {
      console.error('Error creating lesson:', error);
      alert('Failed to create lesson');
    }
  };

  const handleUpdateLesson = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/lessons/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(lessonForm)
      });

      if (response.ok) {
        fetchLessons(currentCourseId);
        setShowLessonModal(false);
        resetLessonForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update lesson');
      }
    } catch (error) {
      console.error('Error updating lesson:', error);
      alert('Failed to update lesson');
    }
  };

  const handleDeleteLesson = async (lessonId, courseId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/lessons/${lessonId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchLessons(courseId);
      } else {
        alert('Failed to delete lesson');
      }
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson');
    }
  };

  // Resource management handlers
  const openResourceModal = async (lesson) => {
    setCurrentLessonId(lesson.id);
    setCurrentCourseId(lesson.course_id);
    setShowResourceModal(true);
    await fetchLessonResources(lesson.id);
  };

  const fetchLessonResources = async (lessonId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/lessons/${lessonId}/resources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLessonResources(data.resources || []);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const handleResourceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const title = prompt('Enter a title for this resource:', file.name);
    if (!title) return;
    
    setResourceUploading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      
      const response = await fetch(`${backendUrl}/api/admin/content/lessons/${currentLessonId}/resources`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        alert('Resource uploaded successfully!');
        await fetchLessonResources(currentLessonId);
        // Refresh lessons to update resource count
        if (currentCourseId) {
          fetchLessons(currentCourseId);
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload resource');
      }
    } catch (error) {
      console.error('Error uploading resource:', error);
      alert('Failed to upload resource');
    } finally {
      setResourceUploading(false);
      if (resourceFileInputRef.current) {
        resourceFileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/lessons/${currentLessonId}/resources/${resourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchLessonResources(currentLessonId);
        // Refresh lessons to update resource count
        if (currentCourseId) {
          fetchLessons(currentCourseId);
        }
      } else {
        alert('Failed to delete resource');
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource');
    }
  };

  // File upload handlers
  const handleFileUpload = async (file, type) => {
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = type === 'video' ? 'upload/video' : type === 'ebook' ? 'upload/ebook' : 'upload/image';
      
      const response = await fetch(`${backendUrl}/api/admin/content/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadProgress(100);
        return data;
      } else {
        const error = await response.json();
        alert(error.detail || 'Upload failed');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Course handlers
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(courseForm)
      });

      if (response.ok) {
        const newCourse = await response.json();
        setCourses([...courses, newCourse]);
        setShowCourseModal(false);
        resetCourseForm();
        alert('Course created successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create course');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      alert('Failed to create course');
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/courses/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(courseForm)
      });

      if (response.ok) {
        const updatedCourse = await response.json();
        setCourses(courses.map(c => c.id === editingItem.id ? updatedCourse : c));
        setShowCourseModal(false);
        setEditingItem(null);
        resetCourseForm();
        alert('Course updated successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update course');
      }
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/courses/${courseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setCourses(courses.filter(c => c.id !== courseId));
        alert('Course deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  // Ebook handlers
  const handleCreateEbook = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const ebookData = {
        ...ebookForm,
        page_count: parseInt(ebookForm.page_count) || 50,
        price: ebookForm.is_premium ? parseFloat(ebookForm.price) || 0 : 0
      };
      
      const response = await fetch(`${backendUrl}/api/admin/content/ebooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(ebookData)
      });

      if (response.ok) {
        const newEbook = await response.json();
        setEbooks([...ebooks, newEbook]);
        setShowEbookModal(false);
        resetEbookForm();
        alert('Ebook created successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create ebook');
      }
    } catch (error) {
      console.error('Error creating ebook:', error);
      alert('Failed to create ebook');
    }
  };

  const handleUpdateEbook = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const ebookData = {
        ...ebookForm,
        page_count: parseInt(ebookForm.page_count) || 50,
        price: ebookForm.is_premium ? parseFloat(ebookForm.price) || 0 : 0
      };
      
      const response = await fetch(`${backendUrl}/api/admin/content/ebooks/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(ebookData)
      });

      if (response.ok) {
        const updatedEbook = await response.json();
        setEbooks(ebooks.map(e => e.id === editingItem.id ? updatedEbook : e));
        setShowEbookModal(false);
        setEditingItem(null);
        resetEbookForm();
        alert('Ebook updated successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update ebook');
      }
    } catch (error) {
      console.error('Error updating ebook:', error);
      alert('Failed to update ebook');
    }
  };

  const handleDeleteEbook = async (ebookId) => {
    if (!window.confirm('Are you sure you want to delete this ebook?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/content/ebooks/${ebookId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setEbooks(ebooks.filter(e => e.id !== ebookId));
        alert('Ebook deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete ebook');
      }
    } catch (error) {
      console.error('Error deleting ebook:', error);
      alert('Failed to delete ebook');
    }
  };

  const openEditCourse = (course) => {
    setEditingItem(course);
    setCourseForm({
      title: course.title,
      description: course.description,
      track: course.track,
      duration: course.duration,
      level: course.level,
      thumbnail_url: course.thumbnail_url || '',
      is_published: course.is_published
    });
    setShowCourseModal(true);
  };

  const openEditEbook = (ebook) => {
    setEditingItem(ebook);
    setEbookForm({
      title: ebook.title,
      author: ebook.author,
      description: ebook.description,
      category: ebook.category,
      page_count: ebook.page_count?.toString() || '50',
      file_url: ebook.file_url,
      thumbnail_url: ebook.thumbnail_url || '',
      is_premium: ebook.is_premium || false,
      price: ebook.price?.toString() || ''
    });
    setShowEbookModal(true);
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      track: 'apostolic-prophetic',
      duration: '',
      level: 'beginner',
      thumbnail_url: '',
      is_published: false
    });
    setEditingItem(null);
  };

  const resetEbookForm = () => {
    setEbookForm({
      title: '',
      author: '',
      description: '',
      category: '',
      page_count: '',
      file_url: '',
      thumbnail_url: '',
      is_premium: false,
      price: ''
    });
    setEditingItem(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      {/* Admin Tour */}
      <TourSystem 
        tourType="admin" 
        showTour={showAdminTour} 
        setShowTour={setShowAdminTour} 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Content Manager</h1>
            <p className="text-gray-600 mt-2">Manage courses, ebooks, users, and email campaigns</p>
          </div>
          <EnhancedTourButton 
            onClick={() => setShowAdminTour(true)} 
            variant="admin" 
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tab.id
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading...</p>
              </div>
            ) : (
              <>
                {/* Welcome Video Tab */}
                {activeTab === 'welcome-video' && (
                  <AdminWelcomeVideo />
                )}

                {/* Courses Tab */}
                {activeTab === 'courses' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">Manage Courses & Lessons</h2>
                      <button 
                        onClick={() => {
                          resetCourseForm();
                          setShowCourseModal(true);
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Course</span>
                      </button>
                    </div>
                    
                    {courses.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No courses found. Create your first course!</p>
                    ) : (
                      <div className="space-y-4">
                        {courses.map((course) => (
                          <div key={course.id} className="border rounded-lg overflow-hidden">
                            {/* Course Header */}
                            <div 
                              className="p-4 bg-white hover:bg-gray-50 cursor-pointer flex justify-between items-start"
                              onClick={() => toggleCourseExpand(course.id)}
                            >
                              <div className="flex items-start space-x-3">
                                <button className="mt-1 text-gray-400">
                                  {expandedCourse === course.id ? (
                                    <ChevronDown className="w-5 h-5" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5" />
                                  )}
                                </button>
                                <div className="flex-1">
                                  <h3 className="font-semibold text-gray-900">{course.title}</h3>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{course.description}</p>
                                  <div className="flex items-center space-x-4 mt-2">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      course.track === 'apostolic-prophetic' 
                                        ? 'bg-purple-100 text-purple-700' 
                                        : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {course.track}
                                    </span>
                                    <span className="text-xs text-gray-500">{course.duration}</span>
                                    <span className="text-xs text-gray-500">{course.level}</span>
                                    <span className="text-xs text-gray-500">
                                      {lessons[course.id]?.length || 0} lessons
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex space-x-2" onClick={e => e.stopPropagation()}>
                                <button 
                                  onClick={() => openEditCourse(course)}
                                  className="p-2 text-gray-600 hover:text-purple-600"
                                  title="Edit Course"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteCourse(course.id)}
                                  className="p-2 text-gray-600 hover:text-red-600"
                                  title="Delete Course"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Lessons Section (Expandable) */}
                            {expandedCourse === course.id && (
                              <div className="border-t bg-gray-50 p-4">
                                <div className="flex justify-between items-center mb-4">
                                  <h4 className="font-medium text-gray-700 flex items-center">
                                    <Play className="w-4 h-4 mr-2" />
                                    Lessons
                                  </h4>
                                  <button 
                                    onClick={() => openAddLesson(course.id)}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center space-x-1"
                                  >
                                    <Plus className="w-3 h-3" />
                                    <span>Add Lesson</span>
                                  </button>
                                </div>
                                
                                {!lessons[course.id] || lessons[course.id].length === 0 ? (
                                  <p className="text-gray-500 text-sm text-center py-4">
                                    No lessons yet. Add your first lesson!
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {lessons[course.id].map((lesson, idx) => (
                                      <div 
                                        key={lesson.id} 
                                        className="flex items-center justify-between bg-white p-3 rounded-lg border"
                                      >
                                        <div className="flex items-center space-x-3">
                                          <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-medium">
                                            {idx + 1}
                                          </span>
                                          <div>
                                            <p className="font-medium text-gray-900 text-sm">{lesson.title}</p>
                                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                                              <span>{lesson.duration_minutes || 0} min</span>
                                              {lesson.drive_file_id && (
                                                <span className="text-green-600 flex items-center">
                                                  <Video className="w-3 h-3 mr-1" />
                                                  Video
                                                </span>
                                              )}
                                              {lesson.resources && lesson.resources.length > 0 && (
                                                <span className="text-blue-600 flex items-center">
                                                  <FileText className="w-3 h-3 mr-1" />
                                                  {lesson.resources.length} file{lesson.resources.length > 1 ? 's' : ''}
                                                </span>
                                              )}
                                              <span className={lesson.is_published ? 'text-green-600' : 'text-orange-600'}>
                                                {lesson.is_published ? 'Published' : 'Draft'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex space-x-1">
                                          <button 
                                            onClick={() => openResourceModal(lesson)}
                                            className="p-1.5 text-gray-500 hover:text-blue-600"
                                            title="Manage Resources"
                                          >
                                            <FileText className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => openLessonExam(lesson, course.id)}
                                            disabled={examLoading === lesson.id}
                                            className="p-1.5 text-gray-500 hover:text-amber-600 disabled:opacity-50"
                                            title="Edit / Add Exam Questions"
                                            data-testid={`lesson-exam-btn-${lesson.id}`}
                                          >
                                            <ListChecks className="w-3 h-3" />
                                          </button>
                                          <button 
                                            onClick={() => openEditLesson(lesson)}
                                            className="p-1.5 text-gray-500 hover:text-purple-600"
                                            title="Edit Lesson"
                                          >
                                            <Edit2 className="w-3 h-3" />
                                          </button>
                                          <button 
                                            onClick={() => handleDeleteLesson(lesson.id, course.id)}
                                            className="p-1.5 text-gray-500 hover:text-red-600"
                                            title="Delete Lesson"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* File Uploads Tab */}
                {activeTab === 'uploads' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-6">File Uploads</h2>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Video Upload Card */}
                      <div className="border rounded-xl p-6 bg-white">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Video className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Upload Video</h3>
                            <p className="text-sm text-gray-500">MP4, WebM, MOV, AVI</p>
                          </div>
                        </div>
                        <input
                          type="file"
                          ref={videoInputRef}
                          className="hidden"
                          accept="video/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const result = await handleFileUpload(file, 'video');
                              if (result) {
                                alert(`Video uploaded!\nURL: ${result.url}`);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploading}
                          className="w-full py-3 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Upload className="w-5 h-5" />
                          <span>{uploading ? 'Uploading...' : 'Choose Video'}</span>
                        </button>
                      </div>

                      {/* Ebook Upload Card */}
                      <div className="border rounded-xl p-6 bg-white">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Upload Ebook</h3>
                            <p className="text-sm text-gray-500">PDF, EPUB</p>
                          </div>
                        </div>
                        <input
                          type="file"
                          ref={ebookFileInputRef}
                          className="hidden"
                          accept=".pdf,.epub"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const result = await handleFileUpload(file, 'ebook');
                              if (result) {
                                alert(`Ebook uploaded!\nURL: ${result.url}`);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => ebookFileInputRef.current?.click()}
                          disabled={uploading}
                          className="w-full py-3 border-2 border-dashed border-orange-300 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Upload className="w-5 h-5" />
                          <span>{uploading ? 'Uploading...' : 'Choose File'}</span>
                        </button>
                      </div>

                      {/* Image Upload Card */}
                      <div className="border rounded-xl p-6 bg-white">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <Image className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Upload Image</h3>
                            <p className="text-sm text-gray-500">JPG, PNG, WebP, GIF</p>
                          </div>
                        </div>
                        <input
                          type="file"
                          ref={imageInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const result = await handleFileUpload(file, 'image');
                              if (result) {
                                alert(`Image uploaded!\nURL: ${result.url}`);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploading}
                          className="w-full py-3 border-2 border-dashed border-green-300 rounded-lg text-green-600 hover:bg-green-50 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Upload className="w-5 h-5" />
                          <span>{uploading ? 'Uploading...' : 'Choose Image'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Google Drive Instructions */}
                    <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
                      <h3 className="font-semibold text-blue-900 mb-2">💡 Using Google Drive for Videos</h3>
                      <p className="text-blue-800 text-sm mb-4">
                        For larger videos, we recommend using Google Drive. Here's how:
                      </p>
                      <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
                        <li>Upload your video to Google Drive</li>
                        <li>Right-click → Share → "Anyone with the link can view"</li>
                        <li>Copy the file ID from the URL (the long string after /d/)</li>
                        <li>Paste the file ID in the "Google Drive File ID" field when adding a lesson</li>
                      </ol>
                    </div>
                  </div>
                )}

                {/* Ebooks Tab */}
                {activeTab === 'ebooks' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">Manage Ebooks</h2>
                      <button 
                        onClick={() => {
                          resetEbookForm();
                          setShowEbookModal(true);
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Ebook</span>
                      </button>
                    </div>
                    
                    {ebooks.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No ebooks found. Add your first ebook!</p>
                    ) : (
                      <div className="grid md:grid-cols-3 gap-4">
                        {ebooks.map((ebook) => (
                          <div key={ebook.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold text-gray-900 text-sm flex-1">{ebook.title}</h3>
                              <div className="flex space-x-1 ml-2">
                                <button 
                                  onClick={() => openEditEbook(ebook)}
                                  className="p-1 text-gray-600 hover:text-orange-600"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteEbook(ebook.id)}
                                  className="p-1 text-gray-600 hover:text-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">{ebook.author}</p>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs px-2 py-1 rounded ${
                                ebook.is_premium 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {ebook.is_premium ? 'Premium' : 'Free'}
                              </span>
                              <span className="text-xs text-gray-500">{ebook.page_count} pages</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-6">Manage Users</h2>
                    
                    {users.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No users found</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                              <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {user.name || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role || 'mentee'}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded ${
                                    user.membership_tier === 'mentorship' 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {user.membership_tier}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.joined_at ? new Date(user.joined_at).toLocaleDateString() : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Email Campaigns Tab */}
                {activeTab === 'emails' && (
                  <div className="text-center py-12">
                    <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Campaigns</h3>
                    <p className="text-gray-500 mb-4">Email campaign system coming soon</p>
                    <p className="text-sm text-gray-400">This feature will allow you to create and send email campaigns to your users</p>
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                      
                      {/* Mentor Mode */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Enable Mentor Mode</h4>
                            <p className="text-sm text-gray-500">
                              Receive email notifications when mentees book appointments. 
                              You will be able to approve, reject, or suggest alternative dates.
                            </p>
                          </div>
                          <button
                            onClick={enableMentorMode}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex-shrink-0"
                          >
                            Enable Mentor Mode
                          </button>
                        </div>
                      </div>

                      {/* Seed Community */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Seed Community Channels</h4>
                            <p className="text-sm text-gray-500">
                              Create default community channels (general, leadership, minstrels, prayer, announcements).
                              Only needed if community chat shows no channels.
                            </p>
                          </div>
                          <button
                            onClick={seedCommunityChannels}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex-shrink-0"
                          >
                            Seed Channels
                          </button>
                        </div>
                      </div>

                      {/* Appointments Link */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Manage Appointments</h4>
                            <p className="text-sm text-gray-500">
                              View pending appointment requests, approve/reject, or suggest alternative dates to mentees.
                            </p>
                          </div>
                          <button
                            onClick={() => navigate('/mentor/appointments')}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium flex-shrink-0"
                          >
                            Go to Appointments
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Course Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingItem ? 'Edit Course' : 'Add New Course'}
                </h2>
                <button 
                  onClick={() => {
                    setShowCourseModal(false);
                    resetCourseForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={editingItem ? handleUpdateCourse : handleCreateCourse}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={courseForm.title}
                      onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={courseForm.description}
                      onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Track *</label>
                      <select
                        value={courseForm.track}
                        onChange={(e) => setCourseForm({...courseForm, track: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      >
                        <option value="apostolic-prophetic">Apostolic-Prophetic</option>
                        <option value="minstrel-worship">Minstrel-Worship</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Level *</label>
                      <select
                        value={courseForm.level}
                        onChange={(e) => setCourseForm({...courseForm, level: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                    <input
                      type="text"
                      value={courseForm.duration}
                      onChange={(e) => setCourseForm({...courseForm, duration: e.target.value})}
                      placeholder="e.g., 8 weeks, 12 months"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
                    <input
                      type="url"
                      value={courseForm.thumbnail_url}
                      onChange={(e) => setCourseForm({...courseForm, thumbnail_url: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={courseForm.is_published}
                      onChange={(e) => setCourseForm({...courseForm, is_published: e.target.checked})}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label className="ml-2 text-sm text-gray-700">Publish immediately</label>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center space-x-2"
                  >
                    <Check className="w-4 h-4" />
                    <span>{editingItem ? 'Update Course' : 'Create Course'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCourseModal(false);
                      resetCourseForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Ebook Modal */}
      {showEbookModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingItem ? 'Edit Ebook' : 'Add New Ebook'}
                </h2>
                <button 
                  onClick={() => {
                    setShowEbookModal(false);
                    resetEbookForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={editingItem ? handleUpdateEbook : handleCreateEbook}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={ebookForm.title}
                      onChange={(e) => setEbookForm({...ebookForm, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Author *</label>
                      <input
                        type="text"
                        value={ebookForm.author}
                        onChange={(e) => setEbookForm({...ebookForm, author: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                      <input
                        type="text"
                        value={ebookForm.category}
                        onChange={(e) => setEbookForm({...ebookForm, category: e.target.value})}
                        placeholder="e.g., Theology, Prayer"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={ebookForm.description}
                      onChange={(e) => setEbookForm({...ebookForm, description: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File URL (Google Drive Link) *</label>
                    <input
                      type="url"
                      value={ebookForm.file_url}
                      onChange={(e) => setEbookForm({...ebookForm, file_url: e.target.value})}
                      placeholder="https://drive.google.com/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Use Google Drive shareable link</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL</label>
                    <input
                      type="url"
                      value={ebookForm.thumbnail_url}
                      onChange={(e) => setEbookForm({...ebookForm, thumbnail_url: e.target.value})}
                      placeholder="https://example.com/cover.jpg"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Count *</label>
                    <input
                      type="number"
                      value={ebookForm.page_count}
                      onChange={(e) => setEbookForm({...ebookForm, page_count: e.target.value})}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={ebookForm.is_premium}
                        onChange={(e) => setEbookForm({...ebookForm, is_premium: e.target.checked})}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label className="ml-2 text-sm text-gray-700">Premium Ebook</label>
                    </div>

                    {ebookForm.is_premium && (
                      <div className="flex-1">
                        <input
                          type="number"
                          value={ebookForm.price}
                          onChange={(e) => setEbookForm({...ebookForm, price: e.target.value})}
                          placeholder="Price (£)"
                          step="0.01"
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-2"
                  >
                    <Check className="w-4 h-4" />
                    <span>{editingItem ? 'Update Ebook' : 'Create Ebook'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEbookModal(false);
                      resetEbookForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingItem ? 'Edit Lesson' : 'Add New Lesson'}
                </h2>
                <button 
                  onClick={() => {
                    setShowLessonModal(false);
                    resetLessonForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={editingItem ? handleUpdateLesson : handleCreateLesson} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lesson Title *</label>
                  <input
                    type="text"
                    value={lessonForm.title}
                    onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})}
                    placeholder="e.g., Introduction to Spiritual Growth"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={lessonForm.description}
                    onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})}
                    placeholder="Brief description of this lesson"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order *</label>
                    <input
                      type="number"
                      value={lessonForm.order}
                      onChange={(e) => setLessonForm({...lessonForm, order: parseInt(e.target.value) || 1})}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                    <input
                      type="number"
                      value={lessonForm.duration_minutes}
                      onChange={(e) => setLessonForm({...lessonForm, duration_minutes: parseInt(e.target.value) || 30})}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                    <Video className="w-4 h-4 mr-2" />
                    Video Source (Google Drive Recommended)
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Google Drive File ID *</label>
                    <input
                      type="text"
                      value={lessonForm.drive_file_id}
                      onChange={(e) => setLessonForm({...lessonForm, drive_file_id: e.target.value})}
                      placeholder="e.g., 1CBnlr5-2I3fForC5xakg6FQI1cTujg4W"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <p className="text-xs text-blue-700 mt-1">
                      Get this from your Google Drive share link: drive.google.com/file/d/<strong>FILE_ID</strong>/view
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Or External Video URL</label>
                  <input
                    type="url"
                    value={lessonForm.video_url}
                    onChange={(e) => setLessonForm({...lessonForm, video_url: e.target.value})}
                    placeholder="https://example.com/video.mp4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional: Use if video is hosted elsewhere</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={lessonForm.is_published}
                    onChange={(e) => setLessonForm({...lessonForm, is_published: e.target.checked})}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label className="ml-2 text-sm text-gray-700">Publish immediately (visible to students)</label>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                  >
                    <Check className="w-4 h-4" />
                    <span>{editingItem ? 'Update Lesson' : 'Create Lesson'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLessonModal(false);
                      resetLessonForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Resource Management Modal */}
      {showResourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Lesson Resources
                </h2>
                <button 
                  onClick={() => {
                    setShowResourceModal(false);
                    setCurrentLessonId(null);
                    setLessonResources([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Upload Section */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New Resource
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Upload PDFs, documents, or other files that students need for this lesson.
                </p>
                <input
                  type="file"
                  ref={resourceFileInputRef}
                  onChange={handleResourceUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip"
                />
                <button
                  onClick={() => resourceFileInputRef.current?.click()}
                  disabled={resourceUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>{resourceUploading ? 'Uploading...' : 'Choose File'}</span>
                </button>
              </div>

              {/* Resources List */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Current Resources</h4>
                {lessonResources.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6 bg-gray-50 rounded-lg">
                    No resources attached to this lesson yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {lessonResources.map((resource) => (
                      <div 
                        key={resource.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center space-x-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{resource.title}</p>
                            <p className="text-xs text-gray-500">{resource.file_name}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteResource(resource.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600"
                          title="Delete Resource"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowResourceModal(false);
                    setCurrentLessonId(null);
                    setLessonResources([]);
                  }}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exam Builder Modal */}
      {examBuilderOpen && examBuilderLesson && (
        <ExamBuilder
          lesson={examBuilderLesson}
          courseId={examBuilderCourseId}
          existingExam={examBuilderExisting}
          onClose={() => {
            setExamBuilderOpen(false);
            setExamBuilderLesson(null);
            setExamBuilderCourseId(null);
            setExamBuilderExisting(null);
          }}
          onSaved={() => {
            // No diagnostic here to refresh — the lesson list already shows independently.
          }}
        />
      )}
    </div>
  );
};

export default AdminContentManager;
